import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/area", label: "Area" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => {
      // On home, keep the transparent-dark navbar until the hero has (nearly) scrolled past.
      // Elsewhere, flip to the solid navbar as soon as the user scrolls a bit.
      const threshold = isHome ? Math.max(300, window.innerHeight * 0.7) : 20;
      setScrolled(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isHome]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const transparent = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/90 backdrop-blur-md border-b border-background-200/70"
      }`}
    >
      <div className="w-full px-3 sm:px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 sm:gap-2.5 group cursor-pointer min-w-0">
          <span
            className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-md bg-accent-500 text-primary-950 shadow-sm transition-transform duration-200 group-hover:-rotate-6 group-hover:scale-105 shrink-0`}
          >
            <i className="ri-flashlight-fill text-base sm:text-lg"></i>
          </span>
          <div className="flex flex-col leading-tight min-w-0">
            <span
              className={`text-sm sm:text-base font-heading font-semibold tracking-tight truncate ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              Watts &amp; Wire
            </span>
            <span
              className={`text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.18em] truncate ${
                transparent ? "text-background-100/80" : "text-foreground-500"
              }`}
            >
              Islington · N. London
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `relative px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap group/nav ${
                  isActive
                    ? transparent
                      ? "text-accent-400"
                      : "text-foreground-950"
                    : transparent
                      ? "text-background-100 hover:text-background-50"
                      : "text-foreground-700 hover:text-foreground-950"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    aria-hidden="true"
                    className={`absolute left-1/2 -translate-x-1/2 bottom-0.5 h-[3px] rounded-full bg-accent-500 transition-all duration-300 ${
                      isActive ? "w-5 opacity-100" : "w-0 opacity-0 group-hover/nav:w-3 group-hover/nav:opacity-70"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href="tel:+447700900123"
            className={`hidden xl:flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              transparent
                ? "text-background-50 hover:text-accent-400"
                : "text-foreground-800 hover:text-primary-700"
            }`}
          >
            <i className="ri-phone-fill text-base"></i>
            07700 900123
          </a>
          <Link
            to="/book"
            className="group flex items-center gap-2 px-4 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
          >
            Book Appointment
            <i className="ri-arrow-right-up-line text-base transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
          </Link>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden w-10 h-10 rounded-md flex items-center justify-center cursor-pointer transition-colors shrink-0 ${
            transparent
              ? "text-background-50 hover:bg-background-50/10"
              : "text-foreground-900 hover:bg-background-100"
          }`}
        >
          <i className={`ri-${open ? "close" : "menu-3"}-line text-2xl`}></i>
        </button>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 border-t border-background-200/70 bg-background-50/95 backdrop-blur-md ${
          open ? "max-h-[560px]" : "max-h-0"
        }`}
      >
        <div className="px-4 py-4 flex flex-col gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `px-3 py-3 rounded-md text-base font-medium cursor-pointer ${
                  isActive
                    ? "bg-background-100 text-foreground-950"
                    : "text-foreground-800 hover:bg-background-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <div className="mt-2 grid grid-cols-2 gap-2">
            <a
              href="tel:+447700900123"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i> Call
            </a>
            <Link
              to="/book"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              Book <i className="ri-arrow-right-up-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}